import { FormattedMessage, FormattedNumber, useIntl } from 'react-intl'
import { useCallback, useMemo, useState } from 'react'
import { useSearchParams } from 'react-router'
import ExternalHeaderContent from '@components/ExternalHeaderContent/ExternalHeaderContent'
import Toast from '@components/Toast/Toast'
import { usePageConfig } from '@hooks/usePageConfig'
import useAuthContact from '@hooks/useAuthContact'
import { useUserAcl } from '@hooks/useUserAcl'
import { isPlanId, type PlanId } from 'src/constants/plans'

import './Pricing.css'

type ContactStatus = 'idle' | 'loading' | 'success' | 'cooldown' | 'error'
type ContactResult = Exclude<ContactStatus, 'idle' | 'loading'>

const CONTACT_TOAST: Record<ContactResult, string> = {
  success: 'pricing.toast.success',
  cooldown: 'pricing.toast.cooldown',
  error: 'pricing.toast.error'
}

const TOAST_DURATION = 4000

const BLOCKED_STATUSES: ContactStatus[] = ['loading', 'success', 'cooldown']

interface Plan {
  id: PlanId
  price: number | null
  period?: 'free' | 'month'
  featured?: boolean
  featureCount: number
}

const PLANS: Plan[] = [
  {
    id: 'preview',
    price: 0,
    period: 'free',
    featureCount: 5
  },
  {
    id: 'explorer',
    price: 59,
    period: 'month',
    featureCount: 6
  },
  {
    id: 'teams',
    price: 299,
    period: 'month',
    featured: true,
    featureCount: 6
  },
  {
    id: 'enterprise',
    price: null,
    featureCount: 5
  }
]

function getCurrentPlan(acl: string[]): PlanId {
  return acl.includes('trial') ? 'preview' : 'teams'
}

const PLAN_QUERY_PARAM = 'plan'

function PlanPrice({ price, period }: Pick<Plan, 'price' | 'period'>) {
  if (price === null) {
    return (
      <p className="pricing-card__price pricing-card__price--custom">
        <FormattedMessage id="pricing.price.custom" />
      </p>
    )
  }

  return (
    <p className="pricing-card__price">
      <span className="pricing-card__amount">
        <FormattedNumber
          value={price}
          style="currency"
          currency="EUR"
          maximumFractionDigits={0}
        />
      </span>
      {period && (
        <span className="pricing-card__period">
          <FormattedMessage id={`pricing.period.${period}`} />
        </span>
      )}
    </p>
  )
}

interface PlanCardProps {
  plan: Plan
  isCurrent: boolean
  isSelected: boolean
  contactStatus: ContactStatus
  onSelect: (id: PlanId) => void
  onContact: (id: PlanId) => void
}

function PlanCard({
  plan,
  isCurrent,
  isSelected,
  contactStatus,
  onSelect,
  onContact
}: PlanCardProps) {
  const base = `pricing.plans.${plan.id}`
  const inputId = `pricing-plan-${plan.id}`

  const className = [
    'pricing-card',
    isCurrent && 'pricing-card--current',
    isSelected && 'pricing-card--selected'
  ]
    .filter(Boolean)
    .join(' ')

  return (
    <article className={className}>
      <input
        type="radio"
        id={inputId}
        name="plan"
        value={plan.id}
        className="pricing-card__radio"
        checked={isSelected}
        disabled={isCurrent}
        onChange={() => onSelect(plan.id)}
      />

      {plan.featured && (
        <span className="pricing-card__badge">
          <FormattedMessage id="pricing.badge.popular" />
        </span>
      )}

      <header className="pricing-card__header">
        <span
          className={`pricing-card__icon icon icon-pricing-${plan.id}`}
          aria-hidden="true"
        />
        <div>
          <h3 className="pricing-card__name">
            <label htmlFor={inputId}>
              <FormattedMessage id={`${base}.name`} />
            </label>
          </h3>
          <p className="pricing-card__description">
            <FormattedMessage id={`${base}.description`} />
          </p>
        </div>
      </header>

      <PlanPrice price={plan.price} period={plan.period} />

      <ul className="pricing-card__features">
        {Array.from({ length: plan.featureCount }, (_, i) => (
          <li key={i} className="pricing-card__feature">
            <span className="icon icon-pricing-check" aria-hidden="true" />
            <span>
              <FormattedMessage id={`${base}.features.${i}`} />
            </span>
          </li>
        ))}
      </ul>

      <div className="pricing-card__footer">
        {isCurrent ? (
          <span className="pricing-card__current">
            <FormattedMessage id="pricing.current" />
          </span>
        ) : isSelected ? (
          <button
            type="button"
            className="pricing-card__cta"
            onClick={() => onContact(plan.id)}
            disabled={BLOCKED_STATUSES.includes(contactStatus)}
            aria-busy={contactStatus === 'loading'}
          >
            <span>
              <FormattedMessage id="pricing.cta.contact" />
            </span>
          </button>
        ) : (
          <span className="pricing-card__hint" aria-hidden="true">
            <FormattedMessage id="select" />
          </span>
        )}
      </div>
    </article>
  )
}

export default function Pricing() {
  const intl = useIntl()
  const [searchParams, setSearchParams] = useSearchParams()

  const acl = useUserAcl()
  const currentPlan = getCurrentPlan(acl)

  const planParam = searchParams.get(PLAN_QUERY_PARAM)
  const selectedPlan =
    isPlanId(planParam) && planParam !== currentPlan ? planParam : null

  const handleSelect = useCallback(
    (id: PlanId) => {
      setSearchParams(
        (prev) => {
          const next = new URLSearchParams(prev)
          next.set(PLAN_QUERY_PARAM, id)
          return next
        },
        { replace: true }
      )
    },
    [setSearchParams]
  )

  const { contact } = useAuthContact('upgrade')
  const [contactStatus, setContactStatus] = useState<ContactStatus>('idle')
  const [toast, setToast] = useState<{ id: number; message: string } | null>(
    null
  )

  const closeToast = useCallback(() => setToast(null), [])

  const finishContact = useCallback(
    (result: ContactResult) => {
      setContactStatus(result)
      setToast((prev) => ({
        id: (prev?.id ?? 0) + 1,
        message: intl.formatMessage({ id: CONTACT_TOAST[result] })
      }))
    },
    [intl]
  )

  const handleContact = useCallback(
    async (id: PlanId) => {
      if (BLOCKED_STATUSES.includes(contactStatus)) return

      setContactStatus('loading')
      try {
        const res = (await contact({ plan: id, source: 'pricing' })) as {
          ok?: boolean
        } | null
        finishContact(res?.ok ? 'success' : 'error')
      } catch (err) {
        const status = (err as { status?: number })?.status
        finishContact(status === 429 ? 'cooldown' : 'error')
      }
    },
    [contact, contactStatus, finishContact]
  )

  const headerContent = useMemo(() => {
    const crumbs = [
      {
        label: intl.formatMessage({ id: 'nav.pricing' }),
        path: '/pricing'
      }
    ]
    return <ExternalHeaderContent hasBack crumbs={crumbs} />
  }, [intl])

  usePageConfig('pricing', headerContent)

  return (
    <section id="pricing-content" className="main-content">
      <div className="container">
        <h1>
          <FormattedMessage id="pricing.subtitle" />
        </h1>
        <p>
          <FormattedMessage id="pricing.text" />
        </p>

        <fieldset className="pricing-grid">
          <legend className="pricing-grid__legend">
            <FormattedMessage id="pricing.legend" />
          </legend>

          {PLANS.map((plan) => (
            <PlanCard
              key={plan.id}
              plan={plan}
              isCurrent={plan.id === currentPlan}
              isSelected={plan.id === selectedPlan}
              contactStatus={contactStatus}
              onSelect={handleSelect}
              onContact={handleContact}
            />
          ))}
        </fieldset>
      </div>

      {toast && (
        <Toast
          key={toast.id}
          message={toast.message}
          duration={TOAST_DURATION}
          onClose={closeToast}
        />
      )}
    </section>
  )
}
